/** Student detail collapse. */
import * as React from 'react';
import {
  User,
  BookOpen,
  Clock,
  Baby,
  GraduationCap,
  MapPin,
  Building2,
  Calendar,
  Mail,
  Phone,
  Heart,
  Accessibility,
  LogOut,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { EmptyPlaceholder } from '@/components/common/details/EmptyPlaceholder';
import { DetailChipList } from '@/components/common/details/DetailChipList';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailAvailabilityGrid } from '@/components/common/details/DetailAvailabilityGrid';
import type { WeeklyAvailabilitySlot } from '@/types/user.types';
import { getSubjects } from '@/services/subjects.service';
import type { Student } from '@/types/inperson/student.types';
import { formatDeactivationDate, getStudentGenderLabel } from '@/utils/common-utils';
import {
  deriveStudentDisplay,
  getStudentStatusDetailColorName,
  getStudentDeactivationDate,
  getStudentExitReasonLabel,
  getStudentDisplayName,
  getSpecialNeedsTypeLabel,
  getSpecialNeedsBadgeLabel,
  buildDisabilityTypeItems,
  resolveStudentSubjectLabels,
  hasStudentPersonalInfo,
} from './student-table-utils';

/** Props for {@link StudentDetails}. */
interface StudentDetailsProps {
  student: Student;
}

/** Student detail collapse. */
export const StudentDetails: React.FC<StudentDetailsProps> = ({ student }) => {
  const { statusKey, statusConfig, StatusIcon, profile, isMinor, hasSpecialNeeds } =
    deriveStudentDisplay(student);

  const { data: allSubjects = [] } = useQuery({
    queryKey: ['subjects'],
    queryFn: async () => {
      const response = await getSubjects();
      return response.data;
    },
  });

  const level = profile?.level;
  const specialNeedsType = profile?.specialNeedsType;
  const specialNeedsNote = profile?.specialNeedsNote;
  const specialNeedsLabel = getSpecialNeedsTypeLabel(specialNeedsType);
  const specialNeedsBadgeLabel = getSpecialNeedsBadgeLabel(
    specialNeedsType,
    specialNeedsLabel,
    specialNeedsNote,
  );
  const isDisabled = profile?.isDisabled;
  const disabilityTypeItems = buildDisabilityTypeItems(
    profile?.disabilityTypes,
    profile?.disabilityOtherNote,
  );
  const subjects = resolveStudentSubjectLabels((profile?.subjects || []) as string[], allSubjects);
  const availability = profile?.availability as { weekly: WeeklyAvailabilitySlot[] } | undefined;
  const dateOfBirth = profile?.dateOfBirth;
  const genderLabel = getStudentGenderLabel(profile?.gender);
  const addressLine1 = profile?.addressLine1;
  const department = profile?.department;
  const postalCode = profile?.postalCode;
  const city = profile?.city;
  const medicalNotes = profile?.medicalNotes;
  const deactivationDate = getStudentDeactivationDate(student);
  const isDeactivated = statusKey === 'deactivated';
  const rawDeactivationReason = student.deactivationReason;
  const deactivationReasonNote = student.deactivationReasonNote;
  const deactivationReasonLabel = getStudentExitReasonLabel(student);
  const establishments = student.userEstablishments || [];
  const showPersonalInfo = hasStudentPersonalInfo({
    dateOfBirth,
    genderLabel,
    addressLine1,
    department,
    postalCode,
    city,
  });

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <User className="w-7 h-7 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">{getStudentDisplayName(student)}</h3>
              <DetailBadge
                label={statusConfig.label}
                icon={StatusIcon}
                colorName={getStudentStatusDetailColorName(statusKey)}
                variant="filled"
              />
              {isMinor && (
                <DetailBadge label="Mineur" icon={Baby} colorName="color-1" variant="outlined" />
              )}
              {hasSpecialNeeds && (
                <DetailBadge
                  label={specialNeedsBadgeLabel}
                  icon={Heart}
                  colorName="color-3"
                  variant="outlined"
                />
              )}
              {isDisabled && (
                <DetailBadge
                  label="En situation de handicap"
                  icon={Accessibility}
                  colorName="color-1"
                  variant="outlined"
                />
              )}
            </div>
            <div className="mt-2 space-y-1">
              {student.email && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  <span>{student.email}</span>
                </div>
              )}
              {student.phone && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone className="w-4 h-4" />
                  <span>{student.phone}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="details-view-grid">
        <DetailsCard icon={User} title="Informations personnelles" colorName="color-1" colSpan={1}>
          <div className="space-y-2 text-sm">
            {dateOfBirth && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">Date de naissance:</span>
                <span>{new Date(dateOfBirth).toLocaleDateString('fr-FR')}</span>
              </div>
            )}
            {genderLabel != null && (
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">Genre:</span>
                <span>{genderLabel}</span>
              </div>
            )}
            {(isDeactivated || deactivationDate) && (
              <div className="flex items-center gap-2">
                <LogOut className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">Date de désactivation:</span>
                <span>{formatDeactivationDate(deactivationDate)}</span>
              </div>
            )}
            {isDeactivated && deactivationReasonLabel !== '-' && (
              <div className="flex items-start gap-2">
                <LogOut className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">Motif de sortie : </span>
                  <span>{deactivationReasonLabel}</span>
                  {(rawDeactivationReason === 'other' || rawDeactivationReason === 'Autre') &&
                    deactivationReasonNote && (
                      <span className="text-muted-foreground"> — {deactivationReasonNote}</span>
                    )}
                </div>
              </div>
            )}
            {(addressLine1 || department || postalCode || city) && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="font-medium">Adresse:</span>
                  <div className="text-sm text-foreground mt-1 space-y-0.5">
                    {addressLine1 && <div>{addressLine1}</div>}
                    {(department || postalCode || city) && (
                      <div>
                        {department && <span>{department}</span>}
                        {department && (postalCode || city) && <span>, </span>}
                        {postalCode && <span>{postalCode}</span>}
                        {postalCode && city && <span> </span>}
                        {city && <span>{city}</span>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {!showPersonalInfo && <EmptyPlaceholder message="Aucune information personnelle" />}
          </div>
        </DetailsCard>

        <DetailsCard icon={GraduationCap} title="Niveau scolaire" colorName="color-1" colSpan={1}>
          {level ? (
            <div className="text-sm font-medium text-foreground">{level.label}</div>
          ) : (
            <EmptyPlaceholder message="Aucun niveau renseigné" />
          )}
        </DetailsCard>

        <DetailsCard icon={Building2} title="Établissements" colorName="color-2" colSpan="full">
          {establishments.length > 0 ? (
            <DetailChipList
              items={establishments.map((ue) => ({
                id: ue.establishment?.id || ue.establishmentId,
                label: ue.establishment?.name || 'Établissement',
              }))}
              colorScheme="color-2"
              variant="filled"
            />
          ) : (
            <EmptyPlaceholder message="Aucun établissement" />
          )}
        </DetailsCard>

        <DetailsCard icon={BookOpen} title="Matières suivies" colorName="color-2" colSpan="full">
          {subjects.length > 0 ? (
            <DetailChipList items={subjects} colorScheme="color-2" variant="filled" />
          ) : (
            <EmptyPlaceholder message="Aucune matière sélectionnée" />
          )}
        </DetailsCard>

        <DetailsCard icon={Clock} title="Disponibilités" colorName="color-1" colSpan="full">
          {availability?.weekly && availability.weekly.length > 0 ? (
            <DetailAvailabilityGrid slots={availability.weekly} variant="compact" />
          ) : (
            <EmptyPlaceholder message="Aucune disponibilité renseignée" />
          )}
        </DetailsCard>

        {isDisabled && (
          <DetailsCard
            icon={Accessibility}
            title="Situation de handicap"
            colorName="color-1"
            colSpan="full"
          >
            {disabilityTypeItems.length > 0 ? (
              <DetailChipList items={disabilityTypeItems} colorScheme="color-1" variant="filled" />
            ) : (
              <EmptyPlaceholder message="Aucun type de handicap renseigné" />
            )}
          </DetailsCard>
        )}

        {medicalNotes && (
          <DetailsCard icon={User} title="Notes" colorName="color-1" colSpan="full">
            <div className="text-sm text-foreground whitespace-pre-wrap">{medicalNotes}</div>
          </DetailsCard>
        )}
      </div>

      {student.createdAt && (
        <div className="mt-8 pt-4 border-t border-[var(--color-1-nuance-3)]">
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <span>Créé le {new Date(student.createdAt).toLocaleDateString('fr-FR')}</span>
            {student.updatedAt && (
              <>
                <span className="text-muted-foreground">•</span>
                <span>Modifié le {new Date(student.updatedAt).toLocaleDateString('fr-FR')}</span>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

/** Factory for the {@link DataTable} expanded-row collapse. */
export const renderStudentDetails = (student: Student): React.ReactNode => {
  return <StudentDetails student={student} />;
};
